import React, { useState, useEffect, useMemo } from 'react';
import { parse, format, isValid } from 'date-fns';

const ScheduledMails = () => {
  const [mailDetails, setMailDetails] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);

  const apiUrl = process.env.REACT_APP_API_URL;
  console.log('API URL:', apiUrl);

  // Custom date parsing function for multiple formats
  const parseCustomDate = (dateString) => {
    if (!dateString) return null;
    
    const formats = [
      'dd-MM-yyyy HH:mm:ss',
      'MM/dd/yyyy hh:mm:ss a',
      'dd/MM/yyyy hh:mm:ss a'
    ];

    for (const formatStr of formats) {
      const parsedDate = parse(dateString, formatStr, new Date());
      if (isValid(parsedDate)) {
        return parsedDate;
      }
    }
    
    return null;
  };

  const formatCustomDate = (date) => {
    if (!date || !isValid(date)) return 'Invalid Date';
    return format(date, 'dd/MM/yyyy hh:mm a');
  };

  // Status badge styling helper
  const getStatusBadgeStyle = (status) => {
    const baseStyle = "px-2 py-1 rounded-full text-xs font-medium text-center";
    switch (status?.toLowerCase()) {
      case 'scheduled':
        return `${baseStyle} bg-yellow-100 text-yellow-800`;
      case 'pending':
        return `${baseStyle} bg-blue-100 text-blue-800`;
      case 'sent':
        return `${baseStyle} bg-green-100 text-green-800`;
      case 'failed':
        return `${baseStyle} bg-red-100 text-red-800`;
      default:
        return `${baseStyle} bg-gray-100 text-gray-800`;
    }
  };

  const BUId = sessionStorage.getItem("BUId");
console.log("BUID",BUId)
  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await fetch(`${apiUrl}/Mailer/GetScheduledMail?BUId=${BUId}`);
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        const data = await response.json();
        // Sort by scheduled date in descending order (latest first)
        const sortedData = data.sort((a, b) => {
          const dateA = parseCustomDate(a.scheduleDate);
          const dateB = parseCustomDate(b.scheduleDate);
          if (!dateA && !dateB) return 0;
          if (!dateA) return 1;
          if (!dateB) return -1;
          return dateB.getTime() - dateA.getTime();
        });
        
        setMailDetails(sortedData);
        setError(null);
      } catch (error) {
        console.error('Error fetching mail details:', error);
        setError('Failed to load scheduled mail details. Please try again later.');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [apiUrl, BUId]);

  // Filter and search functionality
  const filteredMails = useMemo(() => {
    const normalizedSearchTerm = searchTerm.toLowerCase();
  
    return mailDetails.filter(mail => {
      // Ensure mail.status exists before calling toLowerCase()
      const mailStatus = mail.status?.toLowerCase();
  
      // Filter by status 'pending'
      if (mailStatus !== 'pending') {
        return false;
      }
  
      // Apply search term filtering if a search term is provided
      if (!searchTerm) return true;
  
      const matchesReceiver = mail.toEmail.some(email =>
        email.toLowerCase().includes(normalizedSearchTerm)
      );
      const matchesSender = mail.fromEmail.toLowerCase().includes(normalizedSearchTerm);
      const matchesDate = mail.scheduleDate?.toLowerCase().includes(normalizedSearchTerm);
      const matchesSubject = mail.subject?.toLowerCase().includes(normalizedSearchTerm);
  
      return matchesReceiver || matchesSender || matchesDate || matchesSubject;
    });
  }, [mailDetails, searchTerm]);
  
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = filteredMails.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(filteredMails.length / itemsPerPage);

  const paginate = (pageNumber) => setCurrentPage(pageNumber);
console.log("maildetail",mailDetails);
  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-screen bg-gray-100">
        <div className="text-xl font-semibold text-gray-600">Loading...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex justify-center items-center min-h-screen bg-gray-100">
        <div className="text-xl font-semibold text-red-600">{error}</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="container mx-auto bg-white shadow-lg rounded-lg overflow-hidden">
        {/* Header */}
        <div className="bg-gray-200 p-4">
          <h1 className="text-xl text-gray-800">Scheduled Mail Details</h1>
        </div>

        {/* Search Input */}
        <div className="p-4 bg-gray-100 border-b">
          <div className="max-w-md ml-auto relative">
            <input
              type="text"
              placeholder="Search by email, subject, or scheduled date..."
              className="w-full p-3 pl-10 text-gray-700 bg-white rounded-lg shadow-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              value={searchTerm}
              onChange={(e) => {
                setSearchTerm(e.target.value);
                setCurrentPage(1);
              }}
            />
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-6 w-6 absolute left-3 top-3 text-gray-400"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
              />
            </svg>
          </div>
        </div>

        {/* Table */}
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gradient-to-r from-blue-500 to-purple-600">
              <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-white tracking-wider">Campaign Name</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-white tracking-wider">Template Name</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-white tracking-wider">Subject</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-white tracking-wider">To</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-white tracking-wider">From</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-white tracking-wider">Scheduled For</th>
                <th className="px-6 py-3 text-center text-xs font-medium text-white tracking-wider">Status</th>
                {/* <th className="px-6 py-3 text-center text-xs font-medium text-white uppercase tracking-wider">Action</th> */}
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {currentItems.length > 0 ? (
                currentItems.map((mail, index) => (
                  <tr key={index} className="hover:bg-gray-50 transition-colors duration-200">
                   <td className="px-6 py-2 whitespace-nowrap text-sm text-gray-700">{mail.campaignName}</td>
                   <td className="px-6 py-2 whitespace-nowrap text-sm text-gray-700">{mail.templateName}</td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">{mail.subject || 'No Subject'}</div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm text-gray-900">{mail.toEmail}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">{mail.fromEmail}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">
                        {formatCustomDate(parseCustomDate(mail.scheduleDate))}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-center">
                      <span className={getStatusBadgeStyle(mail.status)}>
                        {mail.status || 'Unknown'}
                      </span>
                    </td>
                    {/* <td className="px-6 py-4 whitespace-nowrap text-sm">
                      <button
                         className="bg-transparent hover:bg-blue-100 text-blue-500 font-bold py-2 px-4 rounded-lg transition duration-300 ease-in-out transform hover:scale-105"
                         onClick={() => {
                          const mailLogId = mail.mailLogId;
                          window.location.href = `/viewmaildetails?maillogid=${mailLogId}`;
                        }}
                      >
                        <FontAwesomeIcon icon={faEye} className="text-gray-700 hover:text-blue-500" />
                      </button>
                    </td> */}
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={6} className="px-6 py-4 text-center text-gray-500">
                    No scheduled mails found.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="bg-gray-100 px-4 py-3 flex items-center justify-center space-x-2">
            <button
              onClick={() => paginate(currentPage - 1)}
              disabled={currentPage === 1}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white rounded-lg border border-gray-300 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200"
            >
              Previous
            </button>

            {[...Array(totalPages)].map((_, index) => (
              <button
                key={index}
                onClick={() => paginate(index + 1)}
                className={`px-4 py-2 text-sm font-medium rounded-lg border transition-colors duration-200
                  ${currentPage === index + 1 
                    ? 'bg-blue-500 text-white border-blue-500' 
                    : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                  }`}
              >
                {index + 1}
              </button>
            ))}

            <button
              onClick={() => paginate(currentPage + 1)}
              disabled={currentPage === totalPages}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white rounded-lg border border-gray-300 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200"
            >
              Next
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default ScheduledMails;